import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationExtras, Router } from "@angular/router";
import { UserIdleConfig, UserIdleService } from 'angular-user-idle';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Auth } from '../models/auth.model';
import { DBkeys } from '../models/db-keys';
import { Restrictions } from '../models/restrictions.model';
import { Utilities } from '../models/utilities.model';
import { ConfigurationHelperService } from './configuration-helper.service';
import { JwtHelperService } from './jwt-helper.service';
import { LocalStorageManagerService } from './local-storage-manager.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  public get loginUrl() { return "/login"; }

  private readonly tokenUrl: string = "/api/Authorization/token";

  private readonly scopes: string = 'openid offline_access';
  private readonly clientId: string = 'datafoxapp';

  public loginRedirectUrl: string;
  public logoutRedirectUrl: string;

  public reLoginDelegate: () => void;

  private previousIsLoggedInCheck = false;
  private _loginStatus = new Subject<boolean>();

  private logoutSubscription: Subscription;
  private autoLogoutTimerSubscription1: Subscription;
  private autoLogoutTimerSubscription2: Subscription;

  constructor(private router: Router, private localStorage: LocalStorageManagerService, protected http: HttpClient, private userIdle: UserIdleService) {
    this.initializeLoginStatus();
    // configure time out for user inactivity (logged in)
    let config: UserIdleConfig = this.userIdle.getConfigValue();
    // 60 minutes for inactivity
    config.idle = 60 * 60;
    //config.idle = 10;
    config.ping = 1;
    // 5 seconds timeout to stop logout handling
    config.timeout = 5;
    config.idleSensitivity = 1;
    this.userIdle.setConfigValues(config);

    this.logoutSubscription = this.logoutEmitter.subscribe(logout => {
      if (logout != null) {
        if (logout) {
          this.stopWatching();
        } else {
          this.startWatching();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
    }
    if (this.autoLogoutTimerSubscription1) {
      this.autoLogoutTimerSubscription1.unsubscribe();
    }
    if (this.autoLogoutTimerSubscription2) {
      this.autoLogoutTimerSubscription2.unsubscribe();
    }
  }

  // Restrictions
  public restrictionsChanged: BehaviorSubject<Restrictions> = new BehaviorSubject<Restrictions>(null);
  public set restrictions(value: Restrictions) {
    if (value != this.restrictions) {
      this.restrictionsChanged.next(value);
    }
  }
  public get restrictions(): Restrictions {
    return this.restrictionsChanged.value;
  }
  public loadRestrictions: boolean = false;
  public errorCounter: number = 0;

  public logoutEmitter: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  public startLogout: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  private initializeLoginStatus() {
    this.localStorage.getInitEvent().subscribe(() => {
      this.reevaluateLoginStatus();
    });
  }

  private reevaluateLoginStatus(currentUser?: Auth) {

    let user = currentUser || this.localStorage.getDataObject<Auth>(DBkeys.CURRENT_USER);
    let isLoggedIn = user != null;

    if (this.previousIsLoggedInCheck != isLoggedIn) {
      setTimeout(() => {
        this._loginStatus.next(isLoggedIn);
      });
    }

    this.previousIsLoggedInCheck = isLoggedIn;
  }

  get refreshToken(): string {

    this.reevaluateLoginStatus();
    return this.localStorage.getData(DBkeys.REFRESH_TOKEN);
  }

  get isLoggedIn(): boolean {
    return this.currentUser != null;
  }

  get currentUser(): Auth {

    let user = this.localStorage.getDataObject<Auth>(DBkeys.CURRENT_USER);
    this.reevaluateLoginStatus(user);

    return user;
  }

  private refreshLoginInProgress: boolean = false;
  private accessTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  refreshLogin(next: () => Observable<any> = null) {
    //return this.getRefreshLoginEndpoint()
    //.pipe(map((response: Response) => {
    //    this.processLoginResponse(response, this.rememberMe);
    //}));
    //console.log("refresh token #####");
    if (!this.refreshLoginInProgress) {
      this.refreshLoginInProgress = true;
      this.accessTokenSubject.next(null);
      return this.getRefreshLoginEndpoint()
        .pipe(map((response: Response) => {
          this.refreshLoginInProgress = false;
          this.processLoginResponse(response, this.rememberMe);
        }));
    } else {
      return this.accessTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          return next();
        }));
    }
  }

  getRefreshLoginEndpoint(): Observable<Response> {

    let header = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    let searchParams = new URLSearchParams();
    searchParams.append('refresh_token', this.refreshToken);
    searchParams.append('grant_type', 'refresh_token');
    searchParams.append('scope', this.scopes);
    searchParams.append('client_id', this.clientId);

    let requestBody = searchParams.toString();
    let tokenUrl: string = new Utilities().baseUrl + this.tokenUrl;
    return this.http.post<Response>(tokenUrl, requestBody, { headers: header });
  }

  redirectForLogin() {
    this.loginRedirectUrl = this.router.url;
    this.logout();
    console.log("*** auth-service: redirectForLogin because of error!");
    this.router.navigate([this.loginUrl]);
  }

  reLogin() {
    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);

    if (this.reLoginDelegate) {
      this.reLoginDelegate();
    }
    else {
      this.redirectForLogin();
    }
  }

  get rememberMe(): boolean {
    return this.localStorage.getDataObject<boolean>(DBkeys.REMEMBER_ME) == true;
  }

  private processLoginResponse(response: any, rememberMe: boolean) {

    let accessToken = response.access_token;

    if (accessToken == null)
      throw new Error("Received accessToken was empty");

    let idToken: string = response.id_token;
    let refreshToken: string = response.refresh_token;
    let expiresIn: any = response.expires_in;

    // console.log("Expires in: " + expiresIn);
    let tokenExpiryDate = new Date();
    tokenExpiryDate.setSeconds(tokenExpiryDate.getSeconds() + expiresIn);

    let accessTokenExpiry = tokenExpiryDate;
    // console.log("Expires date: " + tokenExpiryDate);

    let jwtHelper = new JwtHelperService();
    let decodedIdToken = jwtHelper.decodeToken(response.id_token);

    //let permissions: PermissionValues[] = Array.isArray(decodedIdToken.permission) ? decodedIdToken.permission : [decodedIdToken.permission];

    let user = new Auth();
    user.id = decodedIdToken.sub;
    user.userName = decodedIdToken.name;
    user.lastName = decodedIdToken.lastname;
    user.firstName = decodedIdToken.firstname;
    let assignNewPassword: boolean = decodedIdToken.assignnewpassword == "True";
    this.saveUserDetails(user, accessToken, idToken, refreshToken, accessTokenExpiry, assignNewPassword, rememberMe);

    this.reevaluateLoginStatus(user);

    this.logoutEmitter.next(false);
    this.accessTokenSubject.next(idToken);
    return user;
  }

  private saveUserDetails(user: Auth, accessToken: string, idToken: string, refreshToken: string, expiresIn: Date, assignNewPassword: boolean, rememberMe: boolean) {

    if (rememberMe) {
      this.localStorage.savePermanentData(accessToken, DBkeys.ACCESS_TOKEN);
      this.localStorage.savePermanentData(idToken, DBkeys.ID_TOKEN);
      this.localStorage.savePermanentData(refreshToken, DBkeys.REFRESH_TOKEN);
      this.localStorage.savePermanentData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
      //this.localStorage.savePermanentData(permissions, DBkeys.USER_PERMISSIONS);
      this.localStorage.savePermanentData(user, DBkeys.CURRENT_USER);
      this.localStorage.savePermanentData(assignNewPassword, DBkeys.ASSIGNNEWPASSWORD);
    }
    else {
      this.localStorage.saveSyncedSessionData(accessToken, DBkeys.ACCESS_TOKEN);
      this.localStorage.saveSyncedSessionData(idToken, DBkeys.ID_TOKEN);
      this.localStorage.saveSyncedSessionData(refreshToken, DBkeys.REFRESH_TOKEN);
      this.localStorage.saveSyncedSessionData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
      //this.localStorage.saveSyncedSessionData(permissions, DBkeys.USER_PERMISSIONS);
      this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
      this.localStorage.saveSyncedSessionData(assignNewPassword, DBkeys.ASSIGNNEWPASSWORD);
    }

    // console.log("Bearer " + accessToken);
    this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
  }

  public get assignNewPassword(): boolean {
    return this.localStorage.getData(DBkeys.ASSIGNNEWPASSWORD);
  }

  public saveCurrentUser(user: Auth) {
    if (this.rememberMe) {
      this.localStorage.savePermanentData(user, DBkeys.CURRENT_USER);
    } else {
      this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
    }
  }

  redirectLoginUser() {
    let redirect = this.loginRedirectUrl && this.loginRedirectUrl != '/' && this.loginRedirectUrl != ConfigurationHelperService.defaultHomeUrl ? this.loginRedirectUrl : ConfigurationHelperService.defaultHomeUrl;
    this.loginRedirectUrl = null;


    let urlParamsAndFragment = Utilities.splitInTwo(redirect, '#');
    let urlAndParams = Utilities.splitInTwo(urlParamsAndFragment.firstPart, '?');

    let navigationExtras: NavigationExtras = {
      fragment: urlParamsAndFragment.secondPart,
      queryParams: Utilities.getQueryParamsFromString(urlAndParams.secondPart),
      queryParamsHandling: "merge"
    };

    this.router.navigate([urlAndParams.firstPart], navigationExtras);
  }

  getLoginStatusEvent(): Observable<boolean> {
    return this._loginStatus.asObservable();
  }

  get accessTokenExpiryDate(): Date {

    this.reevaluateLoginStatus();
    return this.localStorage.getDataObject<Date>(DBkeys.TOKEN_EXPIRES_IN, true);
  }

  get isSessionExpired(): boolean {

    if (this.accessTokenExpiryDate == null) {
      return true;
    }

    return !(this.accessTokenExpiryDate.valueOf() > new Date().valueOf());
  }

  login(userName: string, password: string, rememberMe?: boolean): Observable<Auth> {

    if (this.isLoggedIn)
      this.logout();

    return this.getLoginEndpoint(userName, password)
      .pipe(map(response => this.processLoginResponse(response, rememberMe)));
  }

  getLoginEndpoint(userName: string, password: string): Observable<Response> {

    let header = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    let searchParams = new URLSearchParams()
    searchParams.append('username', userName);
    searchParams.append('password', password);
    searchParams.append('grant_type', 'password');
    searchParams.append('scope', this.scopes);
    searchParams.append('client_id', this.clientId);
    searchParams.append('resource', window.location.origin);

    let requestBody = searchParams.toString();
    var res = this.http.post<Response>(this.tokenUrl, requestBody, { headers: header });

    return res;
  }

  logout(): Promise<boolean> {
    this.startLogout.next(true);
    return new Promise((resolve, reject) => {
      window.setTimeout(() => {
        //this.logoutToServer().subscribe(res => {

        //}, error => { });
        this.localStorage.deleteData(DBkeys.ACCESS_TOKEN);
        this.localStorage.deleteData(DBkeys.ID_TOKEN);
        this.localStorage.deleteData(DBkeys.REFRESH_TOKEN);
        this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);
        this.localStorage.deleteData(DBkeys.USER_PERMISSIONS);
        this.localStorage.deleteData(DBkeys.CURRENT_USER);

        this.reevaluateLoginStatus();

        this.logoutEmitter.next(true);
        this.accessTokenSubject.next(null);
        resolve(true);
      }, 50);
    });
  }

  private logoutToServer(): Observable<any> {
    let headers = new HttpHeaders({ 'Authorization': 'Bearer ' + this.accessToken });
    return this.http.post<Response>("/api/core/authorization/logout", null, { headers: headers });
  }

  public redirectLogoutUser() {
    let redirect = this.logoutRedirectUrl ? this.logoutRedirectUrl : this.loginUrl;
    this.logoutRedirectUrl = null;

    this.router.navigate([redirect]);
  }

  get accessToken(): string {

    this.reevaluateLoginStatus();
    return this.localStorage.getData(DBkeys.ACCESS_TOKEN);
  }

  // timeout functions for inactivity
  private stop() {
    this.userIdle.stopTimer();
  }

  private stopWatching() {
    //console.log("#### stop watching...");
    this.userIdle.stopWatching();
    if (this.autoLogoutTimerSubscription1) {
      this.autoLogoutTimerSubscription1.unsubscribe();
    }
    if (this.autoLogoutTimerSubscription2) {
      this.autoLogoutTimerSubscription2.unsubscribe();
    }
    this.watchingRuns = false;
  }

  private watchingRuns: boolean = false;
  private startWatching() {
    //console.log("#### start watching...");
    //console.log("### watching runs => ", this.watchingRuns);
    if (!this.watchingRuns) {
      this.userIdle.startWatching();

      // Start watching when user idle is starting.
      // folgende Zeile muss unbedingt drin bleiben, sonst funktioniert das ganze nicht !!!
      this.autoLogoutTimerSubscription1 = this.userIdle.onTimerStart().subscribe(count => console.log(count));
      //this.userIdle.ping$.subscribe(() => console.log("ping"));

      // Start watch when time is up.
      this.autoLogoutTimerSubscription2 = this.userIdle.onTimeout().subscribe(() => {
        console.log('Time is up!');
        this.logout().then(res => {
          this.redirectLogoutUser();
        });
      });
      this.watchingRuns = true;
    } else {
      //console.log("watching runs => no restart ###");
    }
  }

  restart() {
    this.userIdle.resetTimer();
  }
}
